<script lang="ts">
  import RepositoryFactory, { CONTRACT, MNP } from '../../repositories/RepositoryFactory';
  import { loginUser } from '../../components/auth/LoginUser';
  import Loading from '../loader.svelte';
  import type {
    GetContractResult,
    GetPlanChangeStatusResult,
    GetChgMstServiceListResult
  } from '../../repositories/contract';
  import type { GetMnpReserveNumberResult } from '../../repositories/mnp';
  import { isTheBeginningOfTheEndOfTheMonth, isAvailableMnpReserveNumberRequest } from '../../components/Util.svelte';
  import { getContext } from 'svelte';
  const { close }: any = getContext('simple-modal');

  let contract: GetContractResult;
  let planChangeStatus: GetPlanChangeStatusResult;
  let chgMstServiceList: GetChgMstServiceListResult[] = [];
  let mnpReserve: GetMnpReserveNumberResult;
  let isCancel = false;
  let isRequest = false;
  let isComplete = false;
  let changePlan = '';
  let promise = getApi();

  async function getApi() {
    const contractRepository = RepositoryFactory[CONTRACT];
    const mnpRepository = RepositoryFactory[MNP];
    await Promise.all([
      contractRepository.getContract(),
      contractRepository.getPlanChangeStatus(),
      mnpRepository.getMnpReserveNumber()
    ])
      .then((res) => {
        contract = res[0];
        planChangeStatus = res[1];
        mnpReserve = res[2];
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください');
          loginUser.logout();
          location.reload();
        } else {
          alert('エラーが発生しました');
        }
        return false;
      });
    if (planChangeStatus.planCode) {
      // プラン変更キャンセル
      isCancel = true;
    } else {
      //プラン変更依頼
      isRequest = true;
      contractRepository
        .getChgMstServcieList({
          mstServiceId: contract.mstServiceId
        })
        .then((res) => {
          chgMstServiceList = res;
        })
        .catch((error) => {
          if (error.response.status === 403) {
            alert('セッションが切れました。再度ログインしてください');
            loginUser.logout();
            location.reload();
          } else {
            alert('エラーが発生しました');
          }
          return false;
        });
    }
    return { contract };
  }

  function doRequest() {
    if (!changePlan) {
      alert('プランを選択してください');
      return false;
    }
    if (isTheBeginningOfTheEndOfTheMonth()) {
      alert('毎月、月末22時～月初2時はプラン変更/キャンセル依頼はできません');
      return;
    }
    if (mnpReserve && !isAvailableMnpReserveNumberRequest(mnpReserve.issueStatusCode)) {
      alert('MNP予約番号発行中はプラン変更することはできません。');
      return;
    }

    const contractRepository = RepositoryFactory[CONTRACT];
    contractRepository
      .registerPlanChange({ mstServiceCode: changePlan })
      .then((res) => {
        isComplete = true;
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください');
          loginUser.logout();
          location.reload();
        } else {
          alert('プラン変更依頼に失敗しました');
        }
        throw new Error();
      });
  }

  function doCancel() {
    if (isTheBeginningOfTheEndOfTheMonth()) {
      alert('毎月、月末22時～月初2時はプラン変更/キャンセル依頼はできません');
      return;
    }

    const contractRepository = RepositoryFactory[CONTRACT];
    contractRepository
      .cancelPlanChange()
      .then((res) => {
        isComplete = true;
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください');
          loginUser.logout();
          location.reload();
        } else {
          alert('プラン変更キャンセル依頼に失敗しました');
        }
        throw new Error();
      });
  }
</script>

<div class="h-auto p-8 bg-white w-full m-auto">
  <div class="flex justify-between mb-10">
    <div class=" text-gray-700">
      <h1 class="text-2xl font-bold">プラン変更</h1>
    </div>
  </div>

  {#await promise}
    <Loading />
  {:then { contract }}
    {#if isRequest}
      <div class="shadow rounded bg-gray-50 border-1 p-4 border-t-4 border-green-400">
        <div class="flex flex-col">
          {#if !isComplete}
            <div class="items-center min-w-full mb-6">プラン変更を申し込みします</div>

            <div class="flex-1 mb-10">
              <div class="inline-flex justify-items-center items-center min-w-full mb-4">
                <div>現在のプラン：</div>
                <div>{contract.serviceName}</div>
              </div>
              <div class="inline-flex justify-items-center items-center min-w-full">
                <div>変更後のプラン：</div>
                <div>
                  <select
                    id="mstServiceId"
                    name="mstServiceId"
                    class="form-select w-full"
                    bind:value={changePlan}
                    required
                  >
                    {#each chgMstServiceList as chgMstService}
                      <option value={chgMstService.serviceCode}>{chgMstService.serviceName}</option>
                    {/each}
                  </select>
                </div>
              </div>
            </div>
          {:else}
            <div class="flex-1 mt-4 mb-10">
              <div class="justify-items-center text-center min-w-full">プラン変更依頼を受け付けました。</div>
            </div>
          {/if}

          <div class="flex-1">
            <div class="flex flex-col justify-items-center items-center">
              <div class="flex-1 flex-col md:flex-row flex justify-items-center items-center">
                <button class="button-close mb-5" on:click={() => close()}>閉じる</button>
                {#if !isComplete}
                  <button
                    class="button-request md:ml-5 mb-5"
                    disabled={contract.contractStatusCode === 'TERMINATED'}
                    on:click={() => doRequest()}
                  >
                    実行
                  </button>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>
    {:else if isCancel}
      <div class="shadow rounded bg-gray-50 border-1 p-4 border-t-4 border-green-400">
        <div class="flex flex-col">
          {#if !isComplete}
            <div class="items-center min-w-full">プラン変更をキャンセルします。</div>

            <div class="flex-1 mt-4 mb-10">
              <div class="inline-flex flex-col md:flex-row justify-items-center items-center min-w-full">
                <div>変更後のプラン：</div>
                <div>{planChangeStatus.planName}</div>
              </div>
            </div>
          {:else}
            <div class="flex-1 mt-4 mb-10">
              <div class="justify-items-center text-center min-w-full">
                プラン変更のキャンセル依頼を受け付けました。
              </div>
            </div>
          {/if}

          <div class="flex-1">
            <div class="flex flex-col justify-items-center items-center">
              <div class="flex-1 flex-col md:flex-row flex justify-items-center items-center">
                <button class="button-close mb-5" on:click={() => close()}>閉じる</button>
                {#if !isComplete}
                  <button
                    class="button-request md:ml-5 mb-5"
                    disabled={contract.contractStatusCode === 'TERMINATED'}
                    on:click|once={() => doCancel()}
                  >
                    実行
                  </button>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}
  {/await}
</div>

<style lang="postcss">
  .button-close {
    @apply bg-gray-300 rounded hover:bg-gray-400 text-gray-500 focus:outline-none px-14 py-2;
  }
  .button-request {
    @apply bg-green-400 rounded hover:bg-green-500 text-white focus:outline-none px-14 py-2;
  }
  .button-request:disabled {
    @apply opacity-30 cursor-default;
  }
  select {
    @apply border py-2 px-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent;
  }
</style>
